import { useState, useEffect, useCallback } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { Switch } from "@/modern/components/ui/switch";
import { Button } from "@/modern/components/ui/button";
import { Card } from "@/modern/components/ui/card";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { copyTextToClipboard } from "@/other/utils/utils";
import { Copy, Info } from "lucide-react";

const ShareScheduleDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const selectedBoard = project?.boardStore?.first;
  const canEdit = user?.can(GRANTS.grant_four);

  // State for form fields
  const [url, setUrl] = useState("");
  const [passCode, setPassCode] = useState("");
  const [isEntitySharing, setIsEntitySharing] = useState(false);
  const [isAgendaEnabled, setIsAgendaEnabled] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Listen to resourceAgendaStore changes
  useSwivuStore({
    storeId: STORE_KEYS.resourceAgendaStore,
    subscriberFn: () => {
      loadSettings();
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    const resourceAgendaRecord = project?.resourceAgendaStore?.first;
    if (resourceAgendaRecord) {
      // @ts-expect-error - Bryntum model types
      setUrl(resourceAgendaRecord.url || "");
      // @ts-expect-error - Bryntum model types
      setPassCode(resourceAgendaRecord.passCode || "");
      // @ts-expect-error - Bryntum model types
      setIsEntitySharing(resourceAgendaRecord.isEntitySharing || false);
      // @ts-expect-error - Bryntum model types
      setIsAgendaEnabled(resourceAgendaRecord.isAgendaEnabled || false);
      setIsDirty(false);
    }
  };

  const handlePassCodeChange = (value: string) => {
    setPassCode(value);
    setIsDirty(true);
  };

  const handleEntitySharingChange = (value: boolean) => {
    setIsEntitySharing(value);
    setIsDirty(true);
  };

  const handleAgendaEnabledChange = (value: boolean) => {
    setIsAgendaEnabled(value);
    setIsDirty(true);
  };

  const handleSave = async () => {
    const resourceAgendaRecord = project?.resourceAgendaStore?.first;
    if (!resourceAgendaRecord || !canEdit) return;

    // @ts-expect-error - Bryntum model types
    resourceAgendaRecord.passCode = passCode;
    // @ts-expect-error - Bryntum model types
    resourceAgendaRecord.isEntitySharing = isEntitySharing;
    // @ts-expect-error - Bryntum model types
    resourceAgendaRecord.isAgendaEnabled = isAgendaEnabled;

    await project.commitAsync();
    setIsDirty(false);
  };

  const handleReset = () => {
    loadSettings();
  };

  const handleCopyUrl = useCallback(() => {
    if (url) {
      copyTextToClipboard(url, "Link copied to clipboard");
    }
  }, [url]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Board Info */}
        {selectedBoard && (
          <Card className="py-3 px-4 bg-muted/30">
            <div className="flex items-center gap-2">
              <i className="fa-regular fa-clipboard text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">{selectedBoard.name}</span>
            </div>
          </Card>
        )}

        {/* Share URL Card */}
        <Card className="py-4 px-4">
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Public Share Link</h3>
              <p className="text-xs text-muted-foreground">
                Anyone with this link can view the board's schedule
              </p>
            </div>

            <div className="flex gap-2">
              <Input
                value={url}
                disabled
                className="flex-1 bg-muted"
                placeholder="URL will be generated when sharing is enabled"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleCopyUrl}
                disabled={!url}
                className="shrink-0"
              >
                <Copy className="size-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Passcode Card */}
        <Card className="py-4 px-4">
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Access Control</h3>
              <p className="text-xs text-muted-foreground">
                Optional passcode protection (recommended). Case sensitive.
              </p>
            </div>

            <div>
              <Label htmlFor="passcode" className="text-sm font-medium mb-2 block">
                Passcode
              </Label>
              <Input
                id="passcode"
                type="password"
                value={passCode}
                onChange={(e) => handlePassCodeChange(e.target.value)}
                disabled={!canEdit}
                placeholder="Enter an optional passcode"
                className="max-w-md"
              />
            </div>
          </div>
        </Card>

        {/* Sharing Options Card */}
        <Card className="py-4 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Sharing Options</h3>
              <p className="text-xs text-muted-foreground">
                Configure what information is visible to people with the share link
              </p>
            </div>

            <div className="space-y-4">
              {/* Entity Sharing Toggle */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="entity-sharing" className="text-sm font-medium">
                    Include Client and Resource Information
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Show detailed information about clients and resources in the shared schedule
                  </p>
                </div>
                <Switch
                  id="entity-sharing"
                  checked={isEntitySharing}
                  onCheckedChange={handleEntitySharingChange}
                  disabled={!canEdit}
                />
              </div>

              {/* Enable Schedule Sharing Toggle */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="agenda-enabled" className="text-sm font-medium">
                    Enable Schedule Sharing
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Allow others to access this board's schedule via the share link
                  </p>
                </div>
                <Switch
                  id="agenda-enabled"
                  checked={isAgendaEnabled}
                  onCheckedChange={handleAgendaEnabledChange}
                  disabled={!canEdit}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="py-4 px-4 bg-primary/5 border-primary/20">
          <div className="flex items-start gap-3">
            <Info className="size-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-foreground leading-relaxed">
                When schedule sharing is enabled, anyone with the link can view your board's schedule in read-only mode.
                Add a passcode for additional security.
              </p>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        {canEdit && isDirty && (
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleSave}
              variant="default"
              className="gap-2"
            >
              <i className="fa-light fa-save" />
              Save Changes
            </Button>
            <Button
              onClick={handleReset}
              variant="outline"
              className="gap-2"
            >
              <i className="fa-light fa-rotate-left" />
              Reset
            </Button>
          </div>
        )}

        {!canEdit && (
          <Card className="py-3 px-4 bg-primary/5 border-primary/20">
            <div className="flex items-start gap-3">
              <i className="fa-regular fa-lock text-primary mt-0.5" />
              <p className="text-sm text-foreground">
                You don't have permission to modify sharing settings. Contact your board administrator for access.
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ShareScheduleDetail;
